import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, InputAdornment, Tooltip, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UserEditDialog from './UserEditDialog';
import UserCreatedDialog from './UserCreatedDialog';
import {
  listUsers, listGroups, addUserToGroup, removeUserFromGroup,
  updateUserAttributes, setUserEnabled, createUser, forceLogout, resendAccountId
} from '../services/api/userAdminService';

const GROUP_ICONS = {
  'ADMIN': { icon: <AdminPanelSettingsIcon fontSize="small" />, color: 'error', label: 'Administrator' },
  'MODERATOR': { icon: <SupervisorAccountIcon fontSize="small" />, color: 'warning', label: 'Moderator' },
  'OPERATOR': { icon: <EngineeringIcon fontSize="small" />, color: 'info', label: 'Operator' },
  'STANDARD': { icon: <PersonIcon fontSize="small" />, color: 'default', label: 'Standard' },
  'GUEST': { icon: <PublicIcon fontSize="small" />, color: 'disabled', label: 'Guest' },
};

const DEFAULT_GROUP = { icon: <PersonIcon fontSize="small" />, color: 'default', label: null };

export default function UserAdmin() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [dialogMode, setDialogMode] = useState(null); // 'edit' | 'create' | null
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [filter, setFilter] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { message, onConfirm }
  const [paginationToken, setPaginationToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [successDialog, setSuccessDialog] = useState(null); // { accountId, email, emailSent }
  const observerTarget = useRef(null);

  const load = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setUsers([]);
      setPaginationToken(null);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const [userResponse, g] = await Promise.all([
        listUsers(reset ? null : paginationToken),
        reset ? listGroups() : Promise.resolve(groups.map(name => ({ name })))
      ]);

      if (reset) {
        setUsers(userResponse.users);
        setGroups(g.map(g => g.name));
      } else {
        setUsers(prev => [...prev, ...userResponse.users]);
      }

      setPaginationToken(userResponse.paginationToken);
      setHasMore(!!userResponse.paginationToken);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [paginationToken, groups]);

  useEffect(() => { load(true); }, []);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          load(false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, load]);

  const filteredUsers = useMemo(() => {
    if (!filter) return users;
    const q = filter.toLowerCase();
    return users.filter(u =>
      u.email.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      (u.handle && u.handle.toLowerCase().includes(q)) ||
      (u.groups && u.groups.some(g => g.toLowerCase().includes(q)))
    );
  }, [users, filter]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setDialogMode('edit');
    setSaveError(null);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setDialogMode('create');
    setSaveError(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedUser(null);
    setSaveError(null);
  };

  const requireConfirm = (message, onConfirm) => setConfirmAction({ message, onConfirm });

  const handleDialogSave = async (formData) => {
    if (dialogMode === 'create') {
      // Create mode
      await handleCreateUserFromDialog(formData);
    } else {
      // Edit mode
      await handleSaveUserFromDialog(formData);
    }
  };

  const handleCreateUserFromDialog = async (formData) => {
    setSaving(true);
    setSaveError(null);

    try {
      const result = await createUser(
        formData.email,
        formData.password || undefined,
        formData.groups[0], // Primary group
        formData.handle
      );

      // Add other groups if any
      if (formData.groups.length > 1) {
        await Promise.all(
          formData.groups.slice(1).map(g => addUserToGroup(result.accountId, g))
        );
      }

      // Add new user to list
      const newUser = {
        userId: result.userId,
        username: result.accountId,
        email: formData.email,
        handle: formData.handle,
        enabled: true,
        userStatus: 'FORCE_CHANGE_PASSWORD',
        groups: formData.groups
      };
      setUsers(prev => [newUser, ...prev]);

      handleCloseDialog();

      // Show success dialog
      setSuccessDialog({
        accountId: result.accountId,
        email: formData.email,
        emailSent: result.emailSent
      });
    } catch (err) {
      setSaveError(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUserFromDialog = async (formData) => {
    const original = users.find(u => u.username === formData.username);
    const disabling = original.enabled && !formData.enabled;
    const removingAdmin = original.groups.includes('slughorn-administrator') &&
      !formData.groups.includes('slughorn-administrator');

    const doEdit = async () => {
      setSaving(true);
      setSaveError(null);

      // Build optimistic update
      const updatedUser = {
        ...original,
        email: formData.email,
        handle: formData.handle,
        enabled: formData.enabled,
        groups: formData.groups,
        ...formData.attributes
      };

      // Apply optimistic update
      setUsers(prev => prev.map(u => u.username === updatedUser.username ? updatedUser : u));
      handleCloseDialog();

      try {
        // Update attributes if changed
        const changedAttrs = Object.entries(formData.attributes)
          .filter(([k, v]) => String(v) !== String(original[k] ?? ''))
          .map(([Name, Value]) => ({ Name, Value: String(Value) }));

        // Check for email and handle changes
        const emailChanged = formData.email !== original.email;
        const handleChanged = formData.handle !== original.handle;

        if (changedAttrs.length > 0 || emailChanged || handleChanged) {
          const allChanges = [
            ...changedAttrs,
            ...(emailChanged ? [{ Name: 'email', Value: formData.email }] : []),
            ...(handleChanged ? [{ Name: 'custom:handle', Value: formData.handle }] : [])
          ];
          await updateUserAttributes(formData.username, allChanges);
        }

        // Update enabled status if changed
        if (formData.enabled !== original.enabled) {
          await setUserEnabled(formData.username, formData.enabled);
        }

        // Update groups
        const toAdd = formData.groups.filter(g => !original.groups.includes(g));
        const toRemove = original.groups.filter(g => !formData.groups.includes(g));
        await Promise.all([
          ...toAdd.map(g => addUserToGroup(formData.username, g)),
          ...toRemove.map(g => removeUserFromGroup(formData.username, g)),
        ]);
      } catch (e) {
        // Roll back optimistic update
        setUsers(prev => prev.map(u => u.username === original.username ? original : u));
        setDialogMode('edit'); // Reopen dialog
        setSelectedUser(original);
        setSaveError(e.message);
      } finally {
        setSaving(false);
      }
    };

    if (disabling || removingAdmin) {
      const warnings = [
        disabling && 'disable this account',
        removingAdmin && 'remove administrator access',
      ].filter(Boolean).join(' and ');
      requireConfirm(`Are you sure you want to ${warnings} for ${formData.email}?`, doEdit);
    } else {
      doEdit();
    }
  };

  const handleResendAccountId = async (username) => {
    try {
      const result = await resendAccountId(username);
      return result;
    } catch (err) {
      throw err; // Let dialog handle error display
    }
  };

  const handleForceLogout = async (username) => {
    const user = users.find(u => u.username === username);
    if (!user) return;

    try {
      await forceLogout(username);
      alert(`User ${user.email} has been logged out successfully`);
      handleCloseDialog();
    } catch (err) {
      throw err; // Let dialog handle error display
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" action={<Button onClick={() => load(true)}>Retry</Button>}>{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Users ({filteredUsers.length}{filter ? ` of ${users.length}` : ''}{hasMore && !filter ? '+' : ''})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Filter by Account ID, email, handle, or group"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 340 }}
          />
          <Tooltip title="Add User">
            <IconButton
              onClick={handleAddUser}
              color="primary"
            >
              <PersonAddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => load(true)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', flexGrow: 1, overflow: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Account ID</TableCell>
              <TableCell>UUID</TableCell>
              <TableCell>Handle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(user => (
              <TableRow
                key={user.username}
                hover
                onClick={() => handleEdit(user)}
                sx={{ '& td': { py: 0, lineHeight: 1 }, cursor: 'pointer' }}
              >
                <TableCell>{user.email}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>{user.username}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>{user.userId}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'text.secondary' }}>
                  {user.handle || '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title={user.userStatus}>
                    {user.userStatus === 'CONFIRMED'
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <HourglassEmptyIcon fontSize="small" color="warning" />}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={user.enabled ? 'Enabled' : 'Disabled'}>
                    {user.enabled
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <BlockIcon fontSize="small" color="error" />}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {user.groups.map(g => {
                      const { icon, color, label } = GROUP_ICONS[g] || { ...DEFAULT_GROUP, label: g };
                      return (
                        <Tooltip key={g} title={label || g}>
                          <Box sx={{ color: `${color}.main`, display: 'flex' }}>{icon}</Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {user.created ? new Date(user.created).toLocaleDateString() : '-'}
                </TableCell>
              </TableRow>
            ))}
            {/* Infinite scroll trigger */}
            {hasMore && (
              <TableRow ref={observerTarget}>
                <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                  {loadingMore && <CircularProgress size={24} />}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Edit/Create Dialog */}
      <UserEditDialog
        open={!!dialogMode}
        mode={dialogMode}
        user={selectedUser}
        groups={groups}
        onSave={handleDialogSave}
        onCancel={handleCloseDialog}
        onForceLogout={handleForceLogout}
        onResendAccountId={handleResendAccountId}
        saving={saving}
        error={saveError}
      />

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmAction?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setConfirmAction(null); confirmAction.onConfirm(); }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Created Success Dialog */}
      <UserCreatedDialog
        open={!!successDialog}
        data={successDialog}
        onClose={() => setSuccessDialog(null)}
      />
    </Box>
  );
}
